import * as React from 'react';
import { toast } from 'sonner';
import { useNavigate } from '@tanstack/react-router';
import { useNotifications, type NewNotificationPayload } from '@discovr/common';
import { buildNotificationTarget } from './notificationNavigation';

/**
 * Headless bridge: turns enriched `new_notification` SSE events into clickable
 * sonner toasts. Mount once per app, inside both `NotificationProvider` and
 * the router tree (its action's onClick calls `useNavigate`).
 *
 * The panel-open/enrichment suppression (don't toast while the notification
 * panel is already open, and never toast un-enriched legacy payloads) lives in
 * `NotificationContext`'s `registerToastHandler` fan-out — this component just
 * renders whatever the registry hands it.
 */
export function NotificationToastBridge({
  campaignRouteBase = '/campaign',
}: {
  campaignRouteBase?: string;
}) {
  const { registerToastHandler, markAsRead } = useNotifications();
  const navigate = useNavigate();

  React.useEffect(
    () =>
      registerToastHandler((e: NewNotificationPayload) => {
        const title = e.creator_name && e.category === 'message' ? e.creator_name : e.title;
        if (!title) return;
        toast(title, {
          // Passing sonner's dedupe `id` (when we have one) makes repeated SSE
          // events for the same coalesced notification replace the existing
          // toast in place instead of stacking a new one on rapid updates.
          ...(e.notification_id ? { id: e.notification_id } : {}),
          description: e.teaser ?? e.body,
          duration: 6000,
          action: {
            label: 'View',
            onClick: () => {
              if (e.notification_id) void markAsRead(e.notification_id);
              const target = buildNotificationTarget(
                { ...e, count: 1, type: e.type ?? e.action },
                campaignRouteBase,
              );
              if (target) void navigate({ to: target.to, search: target.search as never });
            },
          },
        });
      }),
    [registerToastHandler, markAsRead, navigate, campaignRouteBase],
  );

  return null;
}
